import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, LogIn, User } from "lucide-react";
import { BRAND } from "@/site.config";

const NAV = [
  { label: "Accueil", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 group">
          <img
            src={BRAND.logo}
            alt="SIGNA CLEAN"
            className="h-20 w-20 sm:h-24 sm:w-24 rounded-md object-cover ring-1 ring-white/10 group-hover:ring-[#9DEB30]/60 transition"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((item) => {
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={`relative px-4 py-2 text-sm font-medium transition-colors rounded-md ${
                  active
                    ? "text-[#9DEB30]"
                    : "text-zinc-300 hover:text-white"
                }`}
              >
                {item.label}
                {active && (
                  <span className="absolute left-3 right-3 -bottom-0.5 h-px bg-[#9DEB30]" />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={BRAND.phoneHref}
            data-testid="nav-phone-link"
            className="hidden lg:inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition"
          >
            <Phone size={14} className="text-[#9DEB30]" />
            {BRAND.phone}
          </a>
          <a
            href={BRAND.clientPortal}
            data-testid="nav-client-portal"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-[#9DEB30] border border-[#9DEB30]/40 bg-[#9DEB30]/5 hover:bg-[#9DEB30]/15 hover:border-[#9DEB30]/70 transition-colors"
          >
            <User size={15} />
            Espace client
          </a>
          <a
            href={BRAND.teamPortal}
            data-testid="nav-team-portal"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-zinc-300 border border-white/15 bg-white/5 hover:bg-white/10 hover:text-white hover:border-white/30 transition-colors"
          >
            <LogIn size={15} />
            Accès équipe
          </a>
          <Link
            to="/contact"
            data-testid="nav-cta-quote"
            className="hidden md:inline-flex items-center px-4 py-2 rounded-full text-sm btn-neon"
          >
            Devis gratuit
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden p-2 rounded-md border border-white/10 text-white"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/5 bg-black/95 backdrop-blur-xl">
          <div className="px-5 py-4 flex flex-col gap-2">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                className="px-2 py-3 text-base text-zinc-200 border-b border-white/5"
              >
                {item.label}
              </Link>
            ))}
            <a
              href={BRAND.clientPortal}
              data-testid="nav-mobile-client-portal"
              className="mt-2 flex flex-col px-4 py-3 rounded-lg text-[#9DEB30] border border-[#9DEB30]/40 bg-[#9DEB30]/10"
            >
              <span className="inline-flex items-center gap-2 text-base font-medium">
                <User size={17} /> Espace client
              </span>
              <span className="text-xs text-zinc-400 mt-0.5 pl-7">Vos devis, interventions et documents</span>
            </a>
            <a
              href={BRAND.teamPortal}
              data-testid="nav-mobile-team-portal"
              className="mt-2 flex flex-col px-4 py-3 rounded-lg text-zinc-200 border border-white/15 bg-white/5"
            >
              <span className="inline-flex items-center gap-2 text-base font-medium">
                <LogIn size={17} /> Accès équipe
              </span>
              <span className="text-xs text-zinc-500 mt-0.5 pl-7">Réservé aux collaborateurs</span>
            </a>
            <Link
              to="/contact"
              data-testid="nav-mobile-cta"
              className="mt-2 inline-flex justify-center items-center px-4 py-3 rounded-full text-sm btn-neon"
            >
              Demander un devis gratuit
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
